// Copyright (c) 2024, WSO2 LLC. (https://www.wso2.com). All Rights Reserved.
//
// This software is the property of WSO2 LLC. and its suppliers, if any.
// Dissemination of any information or reproduction of any material contained
// herein in any form is strictly forbidden, unless permitted by WSO2 expressly.
// You may not alter or remove any copyright or other notice from copies of this content.

import { createSlice, PayloadAction, createAsyncThunk } from "@reduxjs/toolkit";
import {
  JobRequisition,
  JobRequisitionChangeStatus,
  JobRequisitionCreate,
  JobRequisitionDelete,
  JobRequisitionResponse,
  JobRequisitionState,
  JobRequisitionUpdate,
} from "./model";
import { APIService } from "@utils/apiService";
import { AppConfig } from "@config/config";
import { enqueueSnackbarMessage } from "@slices/commonSlice/common";
import { SnackMessage } from "@config/constant";
import { HttpStatusCode } from "axios";
import { RequestState } from "@utils/types";

export const getJobRequisitionsById = createAsyncThunk(
  "jobRequisition/getJobRequisitionsById",
  async ({ Id, fromJRView = false }: { Id: number; fromJRView?: boolean }) => {
    return new Promise<any>((resolve, reject) => {
      APIService.getInstance()
        .get(`${AppConfig.serviceUrls.jobRequisition}/${Id}`, {
          params: { fromJRView },
        })
        .then((response) => {
          resolve(response.data);
        })
        .catch((error) => {
          reject(error);
        });
    });
  },
);

export const getJobRequisitions = createAsyncThunk(
  "jobRequisition/getJobRequisitions",
  async ({
    email,
    status,
    limit,
    offset,
    searchString,
  }: {
    email: string;
    status: string[] | null;
    limit: number | null;
    offset: number | null;
    searchString?: string;
  }) => {
    return new Promise<any>((resolve, reject) => {
      APIService.getInstance()
        .get(AppConfig.serviceUrls.jobRequisition, {
          params: {
            email,
            status: status?.join(","),
            limit,
            offset,
            searchString,
          },
        })
        .then((response) => {
          if (response.status === HttpStatusCode.Ok) {
            resolve(response.data);
          } else {
            reject(response);
          }
        })
        .catch((error) => {
          reject(error);
        });
    });
  },
);

export const addJobRequisitions = createAsyncThunk(
  "jobRequisition/addJobRequisitions",
  async ({ payload }: { payload: JobRequisitionCreate }, { dispatch }) => {
    return new Promise<any>((resolve, reject) => {
      APIService.getInstance()
        .post(AppConfig.serviceUrls.jobRequisition, payload)
        .then((response) => {
          resolve(response.data);
          dispatch(
            enqueueSnackbarMessage({
              message: SnackMessage.success.addJR,
              type: "success",
            }),
          );
        })
        .catch((error) => {
          reject(error);
          const errorMessage =
            error.response?.data?.message || SnackMessage.error.addJR;
          dispatch(
            enqueueSnackbarMessage({
              message: errorMessage,
              type: "error",
            }),
          );
        });
    });
  },
);

export const updateJobRequisitionStatusInReview = createAsyncThunk(
  "jobRequisition/updateJobRequisitionStatusInReview",
  async (
    {
      id,
      payload,
    }: {
      id: number;
      payload: JobRequisitionChangeStatus;
    },
    { dispatch },
  ) => {
    return new Promise<any>((resolve, reject) => {
      APIService.getInstance()
        .post(`${AppConfig.serviceUrls.jobRequisition}/${id}/review`, payload)
        .then((response) => {
          resolve(response.data);
        })
        .catch((error) => {
          reject(error);
          dispatch(
            enqueueSnackbarMessage({
              message: SnackMessage.error.updateJR,
              type: "error",
            }),
          );
        });
    });
  },
);

export const updateJobRequisitionStatusApprove = createAsyncThunk(
  "jobRequisition/updateJobRequisitionStatusApprove",
  async (
    {
      id,
      payload,
    }: {
      id: number;
      payload: JobRequisitionChangeStatus;
    },
    { dispatch },
  ) => {
    return new Promise<any>((resolve, reject) => {
      APIService.getInstance()
        .post(`${AppConfig.serviceUrls.jobRequisition}/${id}/approve`, payload)
        .then((response) => {
          resolve(response.data);
          dispatch(
            enqueueSnackbarMessage({
              message: SnackMessage.success.updateJR,
              type: "success",
            }),
          );
        })
        .catch((error) => {
          reject(error);
          dispatch(
            enqueueSnackbarMessage({
              message: SnackMessage.error.updateJR,
              type: "error",
            }),
          );
        });
    });
  },
);

export const updateJobRequisitionStatusReject = createAsyncThunk(
  "jobRequisition/updateJobRequisitionStatusReject",
  async (
    { id, payload }: { id: number; payload: JobRequisitionChangeStatus },
    { dispatch },
  ) => {
    return new Promise<any>((resolve, reject) => {
      APIService.getInstance()
        .post(`${AppConfig.serviceUrls.jobRequisition}/${id}/reject`, payload)
        .then((response) => {
          resolve(response.data);
          dispatch(
            enqueueSnackbarMessage({
              message: SnackMessage.success.updateJR,
              type: "success",
            }),
          );
        })
        .catch((error) => {
          reject(error);
          dispatch(
            enqueueSnackbarMessage({
              message: SnackMessage.error.updateJR,
              type: "error",
            }),
          );
        });
    });
  },
);

export const updateJobRequisitions = createAsyncThunk(
  "jobRequisition/updateJobRequisitions",
  async (
    { id, payload }: { id: number; payload: JobRequisitionUpdate },
    { dispatch },
  ) => {
    return new Promise<any>((resolve, reject) => {
      APIService.getInstance()
        .patch(`${AppConfig.serviceUrls.jobRequisition}/${id}`, payload)
        .then((response) => {
          resolve(response.data);
          dispatch(
            enqueueSnackbarMessage({
              message: SnackMessage.success.updateJR,
              type: "success",
            }),
          );
        })
        .catch((error) => {
          reject(error);
          dispatch(
            enqueueSnackbarMessage({
              message: SnackMessage.error.updateJR,
              type: "error",
            }),
          );
        });
    });
  },
);

export const deleteJobRequisitions = createAsyncThunk(
  "jobRequisition/deleteJobRequisitions",
  async ({ payload }: { payload: JobRequisitionDelete }, { dispatch }) => {
    return new Promise<any>((resolve, reject) => {
      APIService.getInstance()
        .delete(AppConfig.serviceUrls.jobRequisition, { data: payload })
        .then((response) => {
          resolve(response.data);
          dispatch(
            enqueueSnackbarMessage({
              message: SnackMessage.success.deleteJR,
              type: "success",
            }),
          );
        })
        .catch((error) => {
          reject(error);
          dispatch(
            enqueueSnackbarMessage({
              message: SnackMessage.error.deleteJR,
              type: "error",
            }),
          );
        });
    });
  },
);

export const sendJdChangeNotification = createAsyncThunk(
  "jobRequisition/sendJdChangeNotification",
  async (
    { id, action }: { id: number; action: "Accepted" | "Declined" },
    { dispatch },
  ) => {
    return new Promise<any>((resolve, reject) => {
      APIService.getInstance()
        .post(
          `${AppConfig.serviceUrls.jobRequisition}/${id}/change-notification`,
          { action },
        )
        .then((response) => {
          resolve(response.data);
          dispatch(
            enqueueSnackbarMessage({
              message: SnackMessage.success.sendJdChangeNotification,
              type: "success",
            }),
          );
        })
        .catch((error) => {
          reject(error);
          dispatch(
            enqueueSnackbarMessage({
              message: SnackMessage.error.sendJdChangeNotification,
              type: "error",
            }),
          );
        });
    });
  },
);

const initialState: JobRequisitionState = {
  jobRequisitionResponse: {
    jobRequisitions: [],
    totalCount: 0,
  },
  state: RequestState.IDLE,
  updateState: RequestState.IDLE,
  sendJdChangeNotificationState: RequestState.IDLE,
};

const jobRequisitionSlice = createSlice({
  name: "jobRequisition",
  initialState,
  reducers: {
    removeJrFromStoreById: (state, action: PayloadAction<number>) => {
      const before = state.jobRequisitionResponse.jobRequisitions.length;
      state.jobRequisitionResponse.jobRequisitions =
        state.jobRequisitionResponse.jobRequisitions.filter(
          (jr) => jr.id !== action.payload,
        );
      const after = state.jobRequisitionResponse.jobRequisitions.length;
      state.jobRequisitionResponse.totalCount -= Math.max(before - after, 0);
    },
    resetJr: (state) => {
      state.state = RequestState.IDLE;
    },
  },
  extraReducers: (builder) => {
    builder.addCase(getJobRequisitionsById.pending, (state) => {
      state.updateState = RequestState.LOADING;
      state.stateMessage = "Fetching job requisition details...";
    });
    builder.addCase(getJobRequisitionsById.fulfilled, (state) => {
      state.updateState = RequestState.SUCCEEDED;
    });
    builder.addCase(getJobRequisitionsById.rejected, (state) => {
      state.updateState = RequestState.FAILED;
      state.stateMessage = "Failed to fetch Job Requisition. Please try again.";
    });
    builder.addCase(getJobRequisitions.pending, (state) => {
      state.state = RequestState.LOADING;
    });
    builder.addCase(
      getJobRequisitions.fulfilled,
      (state, action: PayloadAction<JobRequisitionResponse>) => {
        state.jobRequisitionResponse.jobRequisitions =
          action.payload.jobRequisitions;
        state.jobRequisitionResponse.totalCount = action.payload.totalCount;
        state.state = RequestState.SUCCEEDED;
      },
    );
    builder.addCase(getJobRequisitions.rejected, (state) => {
      state.state = RequestState.FAILED;
    });

    builder.addCase(addJobRequisitions.pending, (state) => {
      state.updateState = RequestState.LOADING;
    });
    builder.addCase(
      addJobRequisitions.fulfilled,
      (state, action: PayloadAction<JobRequisition>) => {
        state.jobRequisitionResponse.jobRequisitions = [
          action.payload,
          ...state.jobRequisitionResponse.jobRequisitions,
        ];
        state.jobRequisitionResponse.totalCount += 1;
        state.updateState = RequestState.SUCCEEDED;
      },
    );
    builder.addCase(addJobRequisitions.rejected, (state) => {
      state.updateState = RequestState.FAILED;
    });

    builder.addCase(updateJobRequisitions.pending, (state) => {
      state.updateState = RequestState.LOADING;
    });
    builder.addCase(
      updateJobRequisitions.fulfilled,
      (state, action: PayloadAction<JobRequisition>) => {
        const updated = action.payload;
        state.jobRequisitionResponse.jobRequisitions =
          state.jobRequisitionResponse.jobRequisitions.map((jr) =>
            jr.id === updated.id ? updated : jr,
          );
        state.updateState = RequestState.SUCCEEDED;
      },
    );
    builder.addCase(updateJobRequisitions.rejected, (state) => {
      state.updateState = RequestState.FAILED;
    });

    builder.addCase(deleteJobRequisitions.pending, (state) => {
      state.updateState = RequestState.LOADING;
    });
    builder.addCase(
      deleteJobRequisitions.fulfilled,
      (state, action: PayloadAction<number[]>) => {
        const ids = new Set(action.payload);
        const before = state.jobRequisitionResponse.jobRequisitions.length;
        state.jobRequisitionResponse.jobRequisitions =
          state.jobRequisitionResponse.jobRequisitions.filter(
            (jr) => !ids.has(jr.id),
          );
        const after = state.jobRequisitionResponse.jobRequisitions.length;
        state.jobRequisitionResponse.totalCount -= Math.max(before - after, 0);
        state.updateState = RequestState.SUCCEEDED;
      },
    );
    builder.addCase(deleteJobRequisitions.rejected, (state) => {
      state.updateState = RequestState.FAILED;
    });

    builder.addCase(updateJobRequisitionStatusInReview.pending, (state) => {
      state.updateState = RequestState.LOADING;
    });
    builder.addCase(
      updateJobRequisitionStatusInReview.fulfilled,
      (state, action: PayloadAction<JobRequisition>) => {
        const updated = action.payload;
        state.jobRequisitionResponse.jobRequisitions =
          state.jobRequisitionResponse.jobRequisitions.map((jr) =>
            jr.id === updated.id ? updated : jr,
          );
        state.updateState = RequestState.SUCCEEDED;
      },
    );
    builder.addCase(updateJobRequisitionStatusInReview.rejected, (state) => {
      state.updateState = RequestState.FAILED;
    });

    builder.addCase(updateJobRequisitionStatusApprove.pending, (state) => {
      state.updateState = RequestState.LOADING;
    });
    builder.addCase(
      updateJobRequisitionStatusApprove.fulfilled,
      (state, action: PayloadAction<JobRequisition>) => {
        const updated = action.payload;
        state.jobRequisitionResponse.jobRequisitions =
          state.jobRequisitionResponse.jobRequisitions.map((jr) =>
            jr.id === updated.id ? updated : jr,
          );
        state.updateState = RequestState.SUCCEEDED;
      },
    );
    builder.addCase(updateJobRequisitionStatusApprove.rejected, (state) => {
      state.updateState = RequestState.FAILED;
    });

    builder.addCase(updateJobRequisitionStatusReject.pending, (state) => {
      state.updateState = RequestState.LOADING;
    });
    builder.addCase(
      updateJobRequisitionStatusReject.fulfilled,
      (state, action: PayloadAction<JobRequisition>) => {
        const updated = action.payload;
        state.jobRequisitionResponse.jobRequisitions =
          state.jobRequisitionResponse.jobRequisitions.map((jr) =>
            jr.id === updated.id ? updated : jr,
          );
        state.updateState = RequestState.SUCCEEDED;
      },
    );
    builder.addCase(updateJobRequisitionStatusReject.rejected, (state) => {
      state.updateState = RequestState.FAILED;
    });

    builder.addCase(sendJdChangeNotification.pending, (state) => {
      state.sendJdChangeNotificationState = RequestState.LOADING;
      state.stateMessage = "Sending Notification Email...";
    });
    builder.addCase(sendJdChangeNotification.fulfilled, (state) => {
      state.sendJdChangeNotificationState = RequestState.SUCCEEDED;
    });
    builder.addCase(sendJdChangeNotification.rejected, (state) => {
      state.sendJdChangeNotificationState = RequestState.FAILED;
    });
  },
});

export const { removeJrFromStoreById, resetJr } = jobRequisitionSlice.actions;
export default jobRequisitionSlice.reducer;
